import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import type { Theme } from 'webapp/enums/enums';

import type { AnyFilterSort, FilterIdType } from 'webapp/models-custom/filter-sort/base';
import type { AnyFilter } from 'webapp/models-custom/filter-sort/filter';

export interface Args {
	model: AnyFilterSort;
	showJoinedFilterSwitchers?: boolean;
	theme?: Theme | string;
}

interface FilterLineComponentInterface<T> {
	Args: T;
	Blocks: { default: [] }; // this is needed for yield
}

export default abstract class FilterLineOrButtonComponent extends Component<
	FilterLineComponentInterface<Args>
> {
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked isPopupOpen = false;

	node: HTMLElement | null = null;

	get theme() {
		return this.args.theme || '';
	}

	get isSearchActive() {
		return this.navigationService.isFilterSearchActive;
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;
	}

	@action
	teardownView() {
		this.navigationService.isFilterSearchActive = false;
	}

	// -------- Filter options --------
	isFilterTypeSortBySearchPoints(filter: AnyFilter<FilterIdType>) {
		return filter.isSort && this.args.model.isSortBySearchPointsArray;
	}

	get showFilters(): boolean {
		return this.args.model.isAnyFilterShown;
	}

	get isSortBySearchPointsArray() {
		return this.args.model.filters.map((filter) =>
			this.isFilterTypeSortBySearchPoints(filter as AnyFilter<FilterIdType>),
		);
	}

	parseFilterOptionsForPopup(filter: AnyFilter<FilterIdType>): PopupListItem<string>[] {
		return filter.options.map((item) => {
			const extraClass = item.class || '';

			const isSelected = filter.selected.includes(item.id);
			const selectedClass =
				isSelected && !this.isFilterTypeSortBySearchPoints(filter) ? 'selected' : '';

			return {
				id: item.id,
				text: item.text,
				text2: item.text2,
				used: item.used,
				img: item.img,
				color: item.color,
				action: `selectFilter${filter.id}`,
				icon: item.icon,
				class: `${selectedClass} ${extraClass}`,
				isDisabled: !!item.isDisabled,
				extra: {
					statusObject: item.statusObject,
				},
				affixIcon: item.affixIcon,
				text3: item.text3,
				allowDuplicateSelection: item.allowDuplicateSelection,
			};
		});
	}
}
